/**
 * Press-to-talk capture (BIL-032, BR-009).
 *
 * The controller owns one thing the rest of the application must be able to trust: **captured audio
 * exists for as long as it takes to hand it over, and not one moment longer.** Bilal retains no audio
 * (DATA_LIFECYCLE), so the buffer is destroyed and the microphone released on every exit — after a
 * successful transcription, after a failed one, after a thrown one, and on cancel, where the audio is
 * discarded without being sent anywhere at all.
 *
 * The seams mirror the browser APIs rather than wrapping them in a friendlier shape, so the production
 * implementation is a pass-through and the fake in tests is faithful to what the browser really does.
 * The transcript this produces is a *proposal*: it goes to the preview for correction (BR-010/BR-011)
 * and is never committed as a turn on its own.
 */

/** One track of a live microphone stream. Stopping it is what turns the recording indicator off. */
export interface AudioTrackLike {
  stop(): void;
}

export interface MicrophoneStreamLike {
  getTracks(): readonly AudioTrackLike[];
}

/** `MediaRecorder`, narrowed to what capture needs. */
export interface RecorderLike {
  addEventListener(type: 'dataavailable', listener: (event: { readonly data: Blob }) => void): void;
  addEventListener(type: 'stop', listener: () => void): void;
  start(): void;
  stop(): void;
}

export type BrowserSttOutcome =
  | { readonly kind: 'transcribed'; readonly text: string; readonly confidence: number }
  | {
      readonly kind: 'unavailable';
      readonly reason: 'provider_unavailable' | 'rate_limited' | 'unintelligible';
    };

/**
 * The speech-to-text seam, in the browser. The realtime path exchanges a backend-minted single-use
 * token with the provider directly, which is precisely why audio never passes through Bilal's servers.
 */
export interface SttClient {
  transcribe(request: { readonly audio: Blob }): Promise<BrowserSttOutcome>;
}

/**
 * Generic in the stream type so the real `MediaRecorder` can be constructed from the real `MediaStream`
 * without a cast: a cast here would be an unchecked claim at exactly the boundary the browser owns.
 */
export interface CaptureSeams<Stream extends MicrophoneStreamLike = MicrophoneStreamLike> {
  readonly openMicrophone: () => Promise<Stream>;
  readonly createRecorder: (stream: Stream) => RecorderLike;
  readonly stt: SttClient;
  /**
   * How long to wait for the microphone. A browser can leave `getUserMedia` pending forever — neither
   * resolving nor rejecting — and an unbounded await leaves the interface silent with no way back.
   */
  readonly microphoneTimeoutMs: number;
  /** Injected rather than reaching for a timer, so the deadline is deterministic in tests. */
  readonly delay: (ms: number) => Promise<void>;
}

export type CaptureState = 'idle' | 'recording' | 'transcribing';

export type StartOutcome =
  | { readonly kind: 'started' }
  | {
      readonly kind: 'unavailable';
      /** `denied` is a refusal; `unavailable` is a device that never answered at all. */
      readonly reason: 'microphone_denied' | 'microphone_unavailable' | 'already_recording';
    };

export type TranscribeOutcome =
  BrowserSttOutcome | { readonly kind: 'unavailable'; readonly reason: 'no_audio_captured' };

export interface PressToTalk {
  start(): Promise<StartOutcome>;
  stopAndTranscribe(): Promise<TranscribeOutcome>;
  cancel(): void;
  state(): CaptureState;
  /** Observable so a test can assert the buffer is empty rather than trust a comment saying it is. */
  bufferedBytes(): number;
}

export function createPressToTalk<Stream extends MicrophoneStreamLike>(
  seams: CaptureSeams<Stream>,
): PressToTalk {
  /**
   * One live recording, or none. Holding the stream, the recorder and the buffer together means the
   * capture state cannot disagree with itself: there is no "recording" flag that could outlive the
   * stream it describes, and no stream that could survive being forgotten.
   */
  interface Session {
    readonly stream: Stream;
    readonly recorder: RecorderLike;
    readonly stopped: Promise<void>;
  }

  let session: Session | null = null;
  let transcribing = false;
  let opening = false;
  let chunks: Blob[] = [];

  const release = (stream: Stream): void => {
    for (const track of stream.getTracks()) track.stop();
  };

  const handOver = async (audio: Blob): Promise<TranscribeOutcome> => {
    try {
      return await seams.stt.transcribe({ audio });
    } catch {
      // The thrown value could carry request detail; it becomes a code and is not propagated.
      return { kind: 'unavailable', reason: 'provider_unavailable' };
    }
  };

  return {
    state: () => (transcribing ? 'transcribing' : session === null ? 'idle' : 'recording'),
    bufferedBytes: () => chunks.reduce((total, chunk) => total + chunk.size, 0),

    async start(): Promise<StartOutcome> {
      if (session !== null || transcribing || opening) {
        return { kind: 'unavailable', reason: 'already_recording' };
      }
      opening = true;
      let stream: Stream;
      try {
        const pending = seams.openMicrophone();
        // A refused microphone is an ordinary answer, not an exception to propagate into a click
        // handler; a microphone that never answers is a third answer, and needs its own deadline.
        const opened = await Promise.race([
          pending.then(
            (ready) => ({ kind: 'opened' as const, stream: ready }),
            () => ({ kind: 'denied' as const }),
          ),
          seams.delay(seams.microphoneTimeoutMs).then(() => ({ kind: 'timed_out' as const })),
        ]);
        if (opened.kind === 'timed_out') {
          // If the browser answers after the wait was abandoned, close the stream: nothing owns it,
          // and a live microphone with no owner is exactly what a recording indicator warns about.
          void pending.then(
            (late) => {
              release(late);
            },
            () => undefined,
          );
          return { kind: 'unavailable', reason: 'microphone_unavailable' };
        }
        if (opened.kind === 'denied') return { kind: 'unavailable', reason: 'microphone_denied' };
        stream = opened.stream;
      } finally {
        opening = false;
      }
      const recorder = seams.createRecorder(stream);
      recorder.addEventListener('dataavailable', (event) => {
        chunks.push(event.data);
      });
      const stopped = new Promise<void>((resolve) => {
        recorder.addEventListener('stop', () => {
          resolve();
        });
      });
      recorder.start();
      session = { stream, recorder, stopped };
      return { kind: 'started' };
    },

    async stopAndTranscribe(): Promise<TranscribeOutcome> {
      const active = session;
      if (active === null) return { kind: 'unavailable', reason: 'no_audio_captured' };
      transcribing = true;
      try {
        active.recorder.stop();
        await active.stopped;

        // Take the buffer, then forget it and release the microphone *before* anything is handed over.
        // After these four lines the only reference to the audio is the local `captured`, which this
        // function drops on return.
        const captured = chunks;
        chunks = [];
        release(active.stream);
        session = null;

        const first = captured[0];
        if (first === undefined) return { kind: 'unavailable', reason: 'no_audio_captured' };
        const audio = new Blob(captured, { type: first.type });
        // A recorder can deliver an empty final chunk; an empty recording is not something to transcribe.
        if (audio.size === 0) return { kind: 'unavailable', reason: 'no_audio_captured' };
        return await handOver(audio);
      } finally {
        transcribing = false;
      }
    },

    cancel(): void {
      const active = session;
      // Nothing to cancel while idle, and nothing left to cancel mid-transcription: by then the
      // microphone is already released and the buffer already destroyed.
      if (active === null) return;
      active.recorder.stop();
      chunks = [];
      release(active.stream);
      session = null;
    },
  };
}
